import type { ApiClient } from '@/lib/api.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useChatStore } from '@/stores/chatStore.js';
import { eventBus } from '@/lib/events/EventBus.js';
import { setHistoryReplayState } from '@/lib/events/middleware.js';
import type { Message } from '@/types/message.js';

export class SessionService {
    constructor(private apiClient: ApiClient) {}

    async createSession(): Promise<string> {
        const response = await this.apiClient.createSession();
        return response.session.id;
    }

    async switchSession(sessionId: string): Promise<void> {
        console.log('[SessionService] Switching to session:', sessionId);

        // Set loading state
        const chatStore = useChatStore.getState();
        chatStore.setLoadingHistory(sessionId, true);

        try {
            // Fetch session history from backend
            const response = await this.apiClient.getSessionHistory(sessionId);
            const history = response.history || [];

            console.log('[SessionService] Fetched history:', {
                sessionId,
                historyLength: history.length,
                historyPreview: history.slice(0, 2).map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content.slice(0, 50) : '...' }))
            });

            // Update stores atomically
            const sessionStore = useSessionStore.getState();

            // Check current processing state before clearing
            const currentSessionState = chatStore.sessions.get(sessionId);
            const isCurrentlyProcessing = currentSessionState?.processing || false;

            console.log('[SessionService] Preparing session switch:', {
                sessionId,
                isCurrentlyProcessing,
                existingMessageCount: currentSessionState?.messages.length || 0,
            });

            // Clear messages but preserve processing state if agent is actively thinking
            chatStore.clearMessages(sessionId);

            // Only clear processing state if not currently processing
            // (preserve thinking indicator for active sessions)
            if (!isCurrentlyProcessing) {
                chatStore.setProcessing(sessionId, false);
            } else {
                console.log('[SessionService] Preserving processing state - agent is thinking');
            }

            chatStore.setError(sessionId, null);
            chatStore.setStreaming(sessionId, false);

            // Replay history as events through event bus
            // This ensures consistency with live WebSocket events
            console.log('[SessionService] Replaying history as events');
            await this.replayHistoryAsEvents(history, sessionId);

            // Now switch to the session
            sessionStore.setCurrentSession(sessionId);
            sessionStore.setWelcomeState(false);

            // Verify the messages are in the store
            const storedMessages = useChatStore.getState().getMessages(sessionId);
            console.log('[SessionService] Session switched successfully:', {
                currentSessionId: useSessionStore.getState().currentSessionId,
                messagesInStore: storedMessages.length,
                isWelcomeState: useSessionStore.getState().isWelcomeState,
                firstMessage: storedMessages[0] ? { id: storedMessages[0].id, role: storedMessages[0].role } : 'none',
            });
        } finally {
            // Clear loading state
            chatStore.setLoadingHistory(sessionId, false);
        }
    }

    returnToWelcome(): void {
        // Clear all session state completely
        const sessionStore = useSessionStore.getState();
        const chatStore = useChatStore.getState();

        // Reset session state
        sessionStore.setCurrentSession(null);
        sessionStore.setWelcomeState(true);
        sessionStore.setCreatingSession(false);

        // Clear all sessions (no session ID = clear all)
        chatStore.clearMessages();

        console.log('[SessionService] Returned to welcome, state cleared:', {
            currentSessionId: useSessionStore.getState().currentSessionId,
            isWelcomeState: useSessionStore.getState().isWelcomeState,
        });
    }

    /**
     * Replay backend history as events through the event bus
     * This ensures consistency between live WebSocket events and historical loads
     */
    private async replayHistoryAsEvents(history: any[], sessionId: string): Promise<void> {
        const chatStore = useChatStore.getState();

        console.log('[SessionService] Replaying', history.length, 'messages as events', {
            roles: history.map(m => m.role),
        });

        // Suppress notifications during history replay
        setHistoryReplayState(true);

        try {
            for (const msg of history) {
            const content = this.parseContent(msg.content);

            // User messages - add directly (no event for these)
            if (msg.role === 'user') {
                const userMessage: Message = {
                    id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    role: 'user',
                    content: content || '',
                    createdAt: msg.createdAt || Date.now(),
                    sessionId,
                };
                chatStore.addMessage(userMessage);
                continue;
            }

            // Assistant messages - dispatch response event
            if (msg.role === 'assistant') {
                // Dispatch response event (goes through response handler in handlers.ts)
                await eventBus.dispatch({
                    id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    type: 'response',
                    sessionId,
                    text: content || '',
                    tokenUsage: msg.tokenUsage,
                    model: msg.model,
                    provider: msg.provider,
                    router: msg.router,
                    reasoning: msg.reasoning,
                    timestamp: Date.now(),
                });

                // Dispatch tool call events
                if (msg.toolCalls && Array.isArray(msg.toolCalls)) {
                    console.log('[SessionService] Processing', msg.toolCalls.length, 'tool calls from assistant message');

                    for (const tc of msg.toolCalls) {
                        let toolArgs: Record<string, unknown> = {};
                        try {
                            toolArgs = JSON.parse(tc.function?.arguments || '{}');
                        } catch (e) {
                            console.warn('[SessionService] Failed to parse tool args:', e);
                            toolArgs = {};
                        }

                        const toolCallId = tc.id || `tool-${Date.now()}-${Math.random()}`;

                        // Check if this tool had approval by looking at the tool result message
                        const toolResultMsg = history.find(
                            (m: any) => m.role === 'tool' && m.toolCallId === tc.id
                        );

                        // Infer approval status from multiple sources:
                        // 1. Backend may explicitly store requireApproval, approvalId, or approvalStatus
                        // 2. MCP tools (prefixed with mcp__) typically require approval
                        // 3. Check for approval-related metadata fields
                        const toolName = tc.function?.name || '';
                        const isMcpTool = toolName.startsWith('mcp__');
                        const hadApproval = toolResultMsg?.requireApproval ||
                            toolResultMsg?.approvalId ||
                            toolResultMsg?.approvalStatus ||
                            toolResultMsg?.approved ||
                            isMcpTool;

                        console.log('[SessionService] Dispatching toolCall event:', {
                            toolCallId,
                            toolName: tc.function?.name,
                            hasArgs: Object.keys(toolArgs).length > 0,
                            hadApproval: !!hadApproval,
                            backendRequireApproval: toolResultMsg?.requireApproval,
                            backendApprovalStatus: toolResultMsg?.approvalStatus,
                            isMcpToolFallback: isMcpTool && !toolResultMsg?.requireApproval,
                        });

                        // Dispatch tool call event with approval metadata if available
                        await eventBus.dispatch({
                            type: 'toolCall',
                            sessionId,
                            toolCallId,
                            toolName: tc.function?.name || 'unknown',
                            toolArgs,
                            timestamp: Date.now(),
                            // Include approval metadata for history replay
                            // Use backend's stored values if available, otherwise infer from execution
                            ...(hadApproval && {
                                requireApproval: true,
                                approvalStatus: (toolResultMsg?.approvalStatus as 'approved' | 'rejected') || 'approved',
                            }),
                        });

                        // Process tool result if found
                        if (toolResultMsg) {
                            // Backend may send tool results as 'sanitized', 'content', or 'rawResult'
                            const resultData = toolResultMsg.sanitized || toolResultMsg.content || toolResultMsg.rawResult;

                            // Backend always provides explicit success field
                            const toolSuccess = toolResultMsg.success ?? false;

                            console.log('[SessionService] Found tool result:', {
                                toolCallId: tc.id,
                                hasContent: !!toolResultMsg.content,
                                hasSanitized: !!toolResultMsg.sanitized,
                                hasRawResult: !!toolResultMsg.rawResult,
                                success: toolSuccess,
                                resultType: typeof resultData,
                                resultPreview: typeof resultData === 'string'
                                    ? resultData.substring(0, 100)
                                    : JSON.stringify(resultData).substring(0, 100),
                                usingField: toolResultMsg.sanitized ? 'sanitized' : toolResultMsg.content ? 'content' : 'rawResult'
                            });

                            // Dispatch tool result event
                            await eventBus.dispatch({
                                id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                                type: 'toolResult',
                                toolName: tc.function?.name || 'unknown',
                                toolCallId,
                                result: resultData,
                                success: toolSuccess,
                                sessionId,
                                timestamp: Date.now(),
                            });
                        } else {
                            console.warn('[SessionService] ⚠️ No tool result found for toolCallId:', tc.id);
                            console.warn('[SessionService] This will cause the tool to show as "processing" forever');
                        }
                    }
                }

                continue;
            }

            // Tool role messages that aren't matched to toolCalls
            // (standalone tool results or elicitations)
            if (msg.role === 'tool') {
                // Check if this message has approval metadata from backend
                const hasApprovalMetadata = msg.requireApproval || msg.approvalId || msg.type === 'tool_confirmation';
                const approvalStatus = msg.approvalStatus || (msg.success !== false ? 'approved' : 'rejected');

                // Check if this is an elicitation/approval request
                if (msg.type === 'elicitation' || msg.metadata?.schema) {
                    await eventBus.dispatch({
                        id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        type: 'approvalRequest',
                        sessionId,
                        approvalId: msg.approvalId || `approval-${Date.now()}`,
                        approvalType: 'elicitation',
                        metadata: msg.metadata || { schema: {}, prompt: '', serverName: 'agent' },
                        timestamp: Date.now(),
                    });
                } else if (msg.type === 'tool_confirmation') {
                    await eventBus.dispatch({
                        id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        type: 'approvalRequest',
                        sessionId,
                        approvalId: msg.approvalId || `approval-${Date.now()}`,
                        approvalType: 'tool_confirmation',
                        toolName: msg.name || 'unknown',
                        args: msg.args || {},
                        description: msg.description,
                        metadata: {
                            toolName: msg.name || 'unknown',
                            args: msg.args || {},
                            description: msg.description,
                        },
                        timestamp: Date.now(),
                    });
                }

                // If this tool had approval metadata, log it for visibility
                if (hasApprovalMetadata) {
                    console.log('[SessionService] Tool message with approval metadata:', {
                        toolName: msg.name,
                        requireApproval: hasApprovalMetadata,
                        approvalStatus: approvalStatus
                    });
                }
                // Note: Tool results already handled above when processing assistant toolCalls
            }
            }

            console.log('[SessionService] History replay complete');
        } finally {
            // Re-enable notifications after replay
            setHistoryReplayState(false);
        }
    }

    /**
     * Parse content from various backend formats
     */
    private parseContent(content: any): string | null {
        if (typeof content === 'string') {
            return content;
        }
        if (Array.isArray(content)) {
            // Content is array of parts like [{type: 'text', text: '...'}]
            return content
                .filter((part: any) => part.type === 'text' && part.text)
                .map((part: any) => part.text)
                .join('');
        }
        if (content && typeof content === 'object' && content.text) {
            // Single content object
            return content.text;
        }
        return null;
    }
}
